/**
 * Granbay v6 market card — premium size.
 *
 * Founder revision 2026-05-24:
 *   - Bigger overall (more padding, taller header, larger type)
 *   - Inline 24h sparkline next to / below the % chance
 *   - Ochre hover border (var(--color-accent)) — secondary color
 *     of the website, not pure black
 *
 * Layout (matches granbay.com Top Markets):
 *   - Header row: 44×44 cool-gray rounded category icon + 2-line
 *     title clamp on the right
 *   - Optional "↑ TRENDING" mono-ochre accent label
 *   - Big {yes}% chance + "chance" muted label + delta vs first
 *     bucket
 *   - 24h hourly sparkline (uses market.priceHistoryCents)
 *   - 4px progress bar (green YES fill on red-tinted track)
 *   - Outlined YES/NO mono buttons (¢ price, tabular nums)
 *
 * Star bookmark button anchored top-right (sibling of Link to
 * keep the nested-interactive rule happy).
 */
"use client";

import { useState } from "react";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { usePickLocale } from "@/lib/usePickLocale";
import { categoryIcon } from "@/lib/format";
import type { MarketListItem } from "@/lib/api";
import { BookmarkToggleButton } from "@/components/BookmarkToggleButton";

export function V6MarketCard({
  market,
  trending,
}: {
  market: MarketListItem;
  trending?: boolean;
}) {
  const { t } = useTranslation();
  const pick = usePickLocale();
  const title = pick(market.title);
  const yes = market.yesPriceCents;
  const no = yes !== null ? 100 - yes : null;
  const Icon = categoryIcon(market.categorySlug);
  const isBinary = market.kind === "binary" && yes !== null && no !== null;
  const [hover, setHover] = useState(false);

  return (
    <div style={{ position: "relative" }}>
      <BookmarkToggleButton
        marketId={market.id}
        className="absolute right-3 top-3 z-10"
      />
      <Link
        href={`/markets/${market.slug}`}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "block",
          background: "var(--color-surface)",
          borderRadius: 14,
          padding: 20,
          border: `1px solid ${
            hover ? "var(--color-accent)" : "var(--color-border)"
          }`,
          boxShadow: hover
            ? "0 8px 24px rgba(184, 146, 79, 0.15), 0 2px 6px rgba(0, 0, 0, 0.04)"
            : "0 1px 2px rgba(0, 0, 0, 0.02)",
          textDecoration: "none",
          color: "var(--color-text)",
          transition:
            "border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease",
          transform: hover ? "translateY(-2px)" : "translateY(0)",
        }}
      >
        {/* Header: 44×44 category icon + title */}
        <div style={{ display: "flex", gap: 14, marginBottom: 12, paddingRight: 28 }}>
          <div
            aria-hidden="true"
            style={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 12,
              background: "var(--color-bg)",
              color: "var(--color-text2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid var(--color-border)",
            }}
          >
            <Icon size={22} strokeWidth={1.5} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: "var(--font-sans)",
                fontSize: 15,
                fontWeight: 600,
                color: "var(--color-text)",
                lineHeight: 1.32,
                letterSpacing: "-0.01em",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical" as const,
                overflow: "hidden",
              }}
            >
              {title}
            </div>
            {trending && (
              <div
                style={{
                  color: "var(--color-accent)",
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  fontFamily: "var(--font-mono)",
                  marginTop: 5,
                  textTransform: "uppercase",
                }}
              >
                ↑ Trending
              </div>
            )}
          </div>
        </div>

        {/* Big % chance + 24h sparkline */}
        {isBinary && yes !== null && no !== null ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                marginBottom: 10,
                marginTop: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                <span
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 32,
                    fontWeight: 700,
                    color: "var(--color-text)",
                    letterSpacing: "-0.02em",
                    fontVariantNumeric: "tabular-nums",
                    lineHeight: 1,
                  }}
                >
                  {yes}%
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: "var(--color-text3)",
                  }}
                >
                  {t("marketCard.chance", { defaultValue: "chance" })}
                </span>
              </div>
              <Sparkline points={market.priceHistoryCents} fallbackYes={yes} />
            </div>

            {/* Progress bar — green YES fill on red-tinted track */}
            <div
              role="progressbar"
              aria-valuenow={yes}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${yes} percent yes`}
              style={{
                height: 4,
                borderRadius: 999,
                background:
                  "color-mix(in srgb, var(--color-no) 18%, transparent)",
                overflow: "hidden",
                marginBottom: 14,
              }}
            >
              <span
                style={{
                  display: "block",
                  height: "100%",
                  width: `${yes}%`,
                  background: "var(--color-yes)",
                }}
              />
            </div>

            {/* Outlined YES / NO buttons */}
            <div style={{ display: "flex", gap: 8 }}>
              <span
                style={{
                  flex: 1,
                  padding: "10px 9px",
                  borderRadius: 9,
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  background:
                    "color-mix(in srgb, var(--color-yes) 10%, transparent)",
                  color: "var(--color-yes)",
                  border:
                    "1px solid color-mix(in srgb, var(--color-yes) 30%, transparent)",
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                YES {yes}¢
              </span>
              <span
                style={{
                  flex: 1,
                  padding: "10px 9px",
                  borderRadius: 9,
                  textAlign: "center",
                  fontSize: 12,
                  fontWeight: 600,
                  background:
                    "color-mix(in srgb, var(--color-no) 10%, transparent)",
                  color: "var(--color-no)",
                  border:
                    "1px solid color-mix(in srgb, var(--color-no) 30%, transparent)",
                  fontFamily: "var(--font-mono)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                NO {no}¢
              </span>
            </div>
          </>
        ) : (
          <div
            style={{
              fontSize: 12,
              color: "var(--color-text3)",
              padding: "12px 0",
              fontFamily: "var(--font-mono)",
            }}
          >
            {t("marketCard.multiOutcome", {
              defaultValue: "Multi-outcome · open",
            })}
          </div>
        )}
      </Link>
    </div>
  );
}

/**
 * 24h sparkline for a market card.
 *
 * Reads the same hourly-bucketed `priceHistoryCents` series that v1
 * MarketCard's sparkline uses (oldest → newest). Renders as a thin
 * SVG path scaled to a 72×26 box. Stroke color signals direction:
 *   - YES rising → green (matches --color-yes outcome semantics)
 *   - YES falling → red (--color-no)
 *   - Flat / single point / no series → muted gray + subtle dashed
 *     baseline at the current YES level so the card always shows a
 *     trend slot (founder ask 2026-05-24: "every card should show
 *     the trend line"). Closed-beta markets with no trades in the
 *     last 24h would otherwise leave the slot empty.
 */
function Sparkline({
  points,
  fallbackYes,
}: {
  points: number[] | null;
  fallbackYes: number;
}) {
  const w = 72;
  const h = 26;

  // No usable series — draw a faint flat line at the YES level so the
  // sparkline slot is always populated.
  if (!points || points.length < 2) {
    const y = h - (fallbackYes / 100) * h;
    return (
      <svg
        width={w}
        height={h}
        viewBox={`0 0 ${w} ${h}`}
        aria-hidden="true"
        style={{ flexShrink: 0, overflow: "visible" }}
      >
        <path
          d={`M0,${y.toFixed(1)} L${w},${y.toFixed(1)}`}
          fill="none"
          stroke="var(--color-text3)"
          strokeWidth={1.25}
          strokeDasharray="3 3"
          strokeLinecap="round"
          opacity={0.55}
        />
      </svg>
    );
  }

  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const first = points[0]!;
  const last = points[points.length - 1]!;
  const delta = last - first;
  const stroke =
    delta > 0
      ? "var(--color-yes)"
      : delta < 0
        ? "var(--color-no)"
        : "var(--color-text3)";
  const fillTint =
    delta > 0
      ? "color-mix(in srgb, var(--color-yes) 14%, transparent)"
      : delta < 0
        ? "color-mix(in srgb, var(--color-no) 14%, transparent)"
        : "transparent";
  const linePath = points
    .map((p, i) => {
      const x = (i / (points.length - 1)) * w;
      const y = h - ((p - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  // Closed area under the line for a subtle tint.
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      aria-hidden="true"
      style={{ flexShrink: 0, overflow: "visible" }}
    >
      <path d={areaPath} fill={fillTint} stroke="none" />
      <path
        d={linePath}
        fill="none"
        stroke={stroke}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
